import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  History,
  LayoutGrid,
  List,
  Merge,
  MoreHorizontal,
  PawPrint,
  Pencil,
  Plus,
  Star,
  Stethoscope,
  Tag,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { PacienteResponse } from '@/api/generated/model';
import {
  getPacientesControllerListarQueryKey,
  usePacientesControllerExcluir,
  usePacientesControllerListar,
} from '@/api/generated/pacientes/pacientes';
import { useTagsDePacienteControllerListar } from '@/api/generated/tags-de-paciente/tags-de-paciente';
import { ApiError } from '@/api/http-client';
import { BotaoFavorito } from '@/features/pacientes/components/BotaoFavorito';
import { GerenciarTagsDialogo } from '@/features/pacientes/components/GerenciarTagsDialogo';
import { HistoricoJuncoesDialogo } from '@/features/pacientes/components/HistoricoJuncoesDialogo';
import { MesclarPacientesDialogo } from '@/features/pacientes/components/MesclarPacientesDialogo';
import { PacienteCard } from '@/features/pacientes/components/PacienteCard';
import { PacienteFormDialogo } from '@/features/pacientes/components/PacienteFormDialogo';
import { ESPECIES, formatarIdade, SEXOS } from '@/features/pacientes/lib/especies';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDaFichaClinica, rotaDoHistorico } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { TooltipProvider } from '@/shared/ui/tooltip';

const TODAS = 'todas';

type Visualizacao = 'tabela' | 'cards';

/**
 * Lista de pacientes (F5-1).
 *
 * ⚠️ A exclusão é **lógica** no servidor: o paciente sai da lista e o histórico
 * clínico continua apontando para ele. Por isso o texto de confirmação não
 * promete que "os dados serão apagados" — não serão, e não deveriam ser.
 */
export function PacientesPage() {
  const { pode, podeAlguma } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [especie, setEspecie] = useState<string>(TODAS);
  // Filtro, não ordenação: fixar os favoritos no topo exigiria ordenar por uma
  // relação do usuário da sessão, e aí a paginação passaria a depender de quem
  // está olhando. Ligar/desligar a lista curta resolve o mesmo problema sem
  // mexer no contrato de ordenação.
  const [soFavoritos, setSoFavoritos] = useState(false);
  const [tagId, setTagId] = useState<string>(TODAS);
  const [tagsAberto, setTagsAberto] = useState(false);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<PacienteResponse | undefined>();
  const [aExcluir, setAExcluir] = useState<PacienteResponse | undefined>();
  const [aMesclar, setAMesclar] = useState<PacienteResponse | undefined>();
  const [juncoesAberto, setJuncoesAberto] = useState(false);
  // Cards é o padrão, como era a V1 — lá a lista de pacientes SÓ existia em
  // grade. A tabela fica como alternativa para quem precisa varrer muita linha.
  //
  // Estado de componente, não preferência persistida: a escolha vale para a
  // visita. Guardar em localStorage é fácil, mas seria a primeira preferência
  // de usuário desta base e merece decisão explícita, não efeito colateral.
  const [visualizacao, setVisualizacao] = useState<Visualizacao>('cards');

  const { data, isPending, isError, refetch } = usePacientesControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(especie !== TODAS ? { especie: especie as keyof typeof ESPECIES } : {}),
    // Só manda quando ligado: `apenasFavoritos=false` na URL geraria uma
    // segunda chave de cache para a mesma lista de sempre.
    ...(soFavoritos ? { apenasFavoritos: 'true' as const } : {}),
    ...(tagId !== TODAS ? { tagId } : {}),
  });

  const { data: paginaDeTags } = useTagsDePacienteControllerListar({ porPagina: 100 });
  const tags = paginaDeTags?.itens ?? [];

  const excluir = usePacientesControllerExcluir();

  const podeCriar = pode('pacientes.criar');
  const podeEditar = pode('pacientes.editar');
  const podeExcluir = pode('pacientes.excluir');
  const podeVerHistorico = pode('historico.visualizar');
  // Juntar duplicados (o sync do Vertis cria um paciente novo quando nome,
  // tutor ou raça chegam escritos diferente) e o histórico dessas junções.
  const podeMesclar = pode('pacientes.mesclar');
  /**
   * Ficha clínica (diagnósticos + peso).
   *
   * ⚠️ Não basta `pacientes.visualizar`: a tela é de ESCRITA clínica, e quem só
   * tem leitura de cadastro não teria nada a fazer lá — o atalho seria um
   * convite para uma tela de dois blocos vazios. Aparece para quem registra
   * diagnóstico ou quem edita o paciente (que é quem lança peso).
   */
  const podeVerFicha = podeAlguma(['diagnosticos.visualizar', 'pacientes.editar']);

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function confirmarExclusao() {
    if (!aExcluir) return;

    try {
      await excluir.mutateAsync({ id: aExcluir.id });
      await queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
      toast.success(`"${aExcluir.nome}" foi removido da lista.`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir o paciente.');
    } finally {
      setAExcluir(undefined);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome, raça ou microchip..."
            className="sm:max-w-xs"
          />

          <Select value={especie} onValueChange={(v) => aoFiltrar(() => setEspecie(v))}>
            <SelectTrigger className="sm:w-48" aria-label="Filtrar por espécie">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODAS}>Todas as espécies</SelectItem>
              {Object.entries(ESPECIES).map(([valor, rotulo]) => (
                <SelectItem key={valor} value={valor}>
                  {rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={soFavoritos ? 'default' : 'outline'}
            aria-pressed={soFavoritos}
            onClick={() => aoFiltrar(() => setSoFavoritos((valor) => !valor))}
            className="gap-2"
          >
            <Star className={`w-4 h-4 ${soFavoritos ? 'fill-current' : ''}`} />
            Favoritos
          </Button>

          {tags.length > 0 && (
            <Select value={tagId} onValueChange={(v) => aoFiltrar(() => setTagId(v))}>
              <SelectTrigger className="sm:w-48" aria-label="Filtrar por tag">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas as tags</SelectItem>
                {tags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    {tag.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button variant="outline" onClick={() => setTagsAberto(true)} className="gap-2">
            <Tag className="w-4 h-4" />
            Tags
          </Button>

          {podeMesclar && (
            <Button variant="outline" onClick={() => setJuncoesAberto(true)} className="gap-2">
              <History className="w-4 h-4" />
              Histórico de junções
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* `aria-pressed` em vez de dois estados visuais só: leitor de tela
              precisa saber QUAL visualização está ativa, não só que há duas. */}
          <div className="flex rounded-md border border-border overflow-hidden">
            <Button
              variant={visualizacao === 'tabela' ? 'default' : 'ghost'}
              size="icon"
              className="rounded-none"
              aria-label="Ver em tabela"
              aria-pressed={visualizacao === 'tabela'}
              onClick={() => setVisualizacao('tabela')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={visualizacao === 'cards' ? 'default' : 'ghost'}
              size="icon"
              className="rounded-none"
              aria-label="Ver em cards"
              aria-pressed={visualizacao === 'cards'}
              onClick={() => setVisualizacao('cards')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>

          {podeCriar && (
            <Button
              onClick={() => {
                setEmEdicao(undefined);
                setFormAberto(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Novo paciente
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Pacientes</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={soFavoritos ? Star : PawPrint}
            titulo={soFavoritos ? 'Nenhum favorito ainda' : 'Nenhum paciente encontrado'}
            descricao={
              soFavoritos
                ? 'Marque a estrela de um paciente para tê-lo sempre à mão nesta lista.'
                : busca || especie !== TODAS
                  ? 'Tente outro termo ou limpe os filtros.'
                  : 'Cadastre o primeiro paciente para começar a lançar exames e prontuários.'
            }
          />
        ) : (
          <>
            {visualizacao === 'cards' ? (
              <TooltipProvider>
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {data.itens.map((paciente) => (
                    <PacienteCard
                      key={paciente.id}
                      paciente={paciente}
                      podeEditar={podeEditar}
                      podeExcluir={podeExcluir}
                      podeVerHistorico={podeVerHistorico}
                      podeVerFicha={podeVerFicha}
                      podeMesclar={podeMesclar}
                      aoMesclar={setAMesclar}
                      aoEditar={(p) => {
                        setEmEdicao(p);
                        setFormAberto(true);
                      }}
                      aoExcluir={setAExcluir}
                    />
                  ))}
                </div>
              </TooltipProvider>
            ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <span className="sr-only">Favorito</span>
                    </TableHead>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Espécie</TableHead>
                    <TableHead>Idade</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Registros</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((paciente) => (
                    <TableRow key={paciente.id}>
                      <TableCell>
                        <BotaoFavorito paciente={paciente} />
                      </TableCell>
                      <TableCell>
                        {podeVerHistorico ? (
                          <Link
                            to={rotaDoHistorico(paciente.id)}
                            className="font-medium text-foreground hover:text-brand transition-colors"
                          >
                            {paciente.nome}
                          </Link>
                        ) : (
                          <span className="font-medium text-foreground">{paciente.nome}</span>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[paciente.raca, SEXOS[paciente.sexo]].filter(Boolean).join(' · ')}
                          {paciente.castrado && ' · castrado'}
                        </p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {ESPECIES[paciente.especie]}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatarIdade(paciente.idadeMeses)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {paciente.tutorNome ?? '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          <Badge variant="outline" className="font-normal">
                            {paciente.totalExames} exames
                          </Badge>
                          <Badge variant="outline" className="font-normal">
                            {paciente.totalProntuarios} prontuários
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${paciente.nome}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {podeVerHistorico && (
                              <DropdownMenuItem asChild>
                                <Link to={rotaDoHistorico(paciente.id)}>
                                  <Activity className="w-4 h-4" />
                                  Ver histórico
                                </Link>
                              </DropdownMenuItem>
                            )}

                            {podeVerFicha && (
                              <DropdownMenuItem asChild>
                                <Link to={rotaDaFichaClinica(paciente.id)}>
                                  <Stethoscope className="w-4 h-4" />
                                  Ficha clínica
                                </Link>
                              </DropdownMenuItem>
                            )}

                            {podeEditar && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setEmEdicao(paciente);
                                  setFormAberto(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                                Editar
                              </DropdownMenuItem>
                            )}

                            {podeMesclar && (
                              <DropdownMenuItem onClick={() => setAMesclar(paciente)}>
                                <Merge className="w-4 h-4" />
                                Juntar com outro paciente
                              </DropdownMenuItem>
                            )}

                            {podeExcluir && (
                              <DropdownMenuItem
                                onClick={() => setAExcluir(paciente)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            )}

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <PacienteFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { paciente: emEdicao } : {})}
      />

      <GerenciarTagsDialogo aberto={tagsAberto} aoFechar={() => setTagsAberto(false)} />

      {aMesclar && (
        <MesclarPacientesDialogo
          aberto
          aoFechar={() => setAMesclar(undefined)}
          paciente={aMesclar}
        />
      )}

      <HistoricoJuncoesDialogo aberto={juncoesAberto} aoFechar={() => setJuncoesAberto(false)} />

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {aExcluir?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              O paciente sai da lista, mas os exames e prontuários já lançados continuam no
              histórico da clínica. Esta ação não pode ser desfeita pela tela.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarExclusao()}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
